/**
 * Formula data source for the progress meter block.
 *
 * Lets a campaign owner compose the meter's current value from live campaign
 * metrics and constants with + − × ÷ — e.g. "סכום התרומות ÷ 45" to show how
 * many units (meals, trees, …) the raised amount covers.
 *
 * The formula is a flat list of operands with operators between them. We keep
 * it as plain data (no expression string) so there's never any eval() of user
 * input, and the builder UI can render/edit it directly.
 *   terms.length === ops.length + 1
 */

export type FormulaMetric = 'raised' | 'donationCount' | 'recurringCount';
export type FormulaOp = '+' | '-' | '*' | '/';
export type FormulaOperand =
  | { type: 'metric'; metric: FormulaMetric }
  | { type: 'const'; value: number };

/** How the final result is rounded — useful when a division doesn't come out
 *  even (e.g. "כמה פעמים סכום התרומות נכנס ב-45"). */
export type FormulaRounding = 'round' | 'floor' | 'ceil' | 'none';

export interface Formula {
  terms: FormulaOperand[];
  ops: FormulaOp[];
  rounding: FormulaRounding;
}

/** Live campaign metrics the formula can reference. */
export const FORMULA_METRICS: { value: FormulaMetric; label: string }[] = [
  { value: 'raised', label: 'סכום תרומות' },
  { value: 'donationCount', label: 'מספר תרומות' },
  { value: 'recurringCount', label: 'הוראות קבע' },
];

/** Display symbols use ×/÷ (math), evaluation uses the JS operators. */
export const FORMULA_OPS: { value: FormulaOp; label: string }[] = [
  { value: '+', label: '+' },
  { value: '-', label: '−' },
  { value: '*', label: '×' },
  { value: '/', label: '÷' },
];

export const FORMULA_ROUNDINGS: { value: FormulaRounding; label: string }[] = [
  { value: 'round', label: 'עיגול לשלם הקרוב' },
  { value: 'floor', label: 'עיגול כלפי מטה (כמה נכנס שלם)' },
  { value: 'ceil', label: 'עיגול כלפי מעלה' },
  { value: 'none', label: 'ללא עיגול (כולל שבר)' },
];

export const DEFAULT_FORMULA: Formula = {
  terms: [{ type: 'metric', metric: 'raised' }],
  ops: [],
  rounding: 'round',
};

const OPS: FormulaOp[] = ['+', '-', '*', '/'];
const ROUNDINGS: FormulaRounding[] = ['round', 'floor', 'ceil', 'none'];

/** Coerce any stored/partial value into a well-formed Formula. */
export function normalizeFormula(f: any): Formula {
  if (!f || !Array.isArray(f.terms) || f.terms.length === 0) return DEFAULT_FORMULA;
  const terms: FormulaOperand[] = f.terms.map((t: any) =>
    t && t.type === 'const'
      ? { type: 'const', value: Number(t.value) || 0 }
      : { type: 'metric', metric: (t && t.metric) || 'raised' });
  const ops: FormulaOp[] = (Array.isArray(f.ops) ? f.ops : [])
    .slice(0, terms.length - 1)
    .map((o: any) => (OPS.includes(o) ? o : '+'));
  while (ops.length < terms.length - 1) ops.push('+');
  // Default to 'round' so formulas saved before rounding existed keep their
  // prior (Math.round) behavior.
  const rounding: FormulaRounding = ROUNDINGS.includes(f.rounding) ? f.rounding : 'round';
  return { terms, ops, rounding };
}

/** Apply the formula's rounding mode to a raw result. */
export function applyRounding(value: number, rounding: FormulaRounding): number {
  switch (rounding) {
    case 'floor': return Math.floor(value);
    case 'ceil': return Math.ceil(value);
    case 'none': return value;
    case 'round':
    default: return Math.round(value);
  }
}

function operandLabel(op: FormulaOperand): string {
  if (op.type === 'const') return String(op.value);
  return FORMULA_METRICS.find(m => m.value === op.metric)?.label || op.metric;
}

/** Human-readable rendering, e.g. "סכום תרומות ÷ 45". */
export function formatFormula(f: any): string {
  const nf = normalizeFormula(f);
  let s = operandLabel(nf.terms[0]);
  for (let i = 0; i < nf.ops.length; i++) {
    const opLabel = FORMULA_OPS.find(o => o.value === nf.ops[i])?.label || nf.ops[i];
    s += ` ${opLabel} ${operandLabel(nf.terms[i + 1])}`;
  }
  return s;
}

/**
 * Evaluate the formula against live metric values. Honors standard precedence
 * (× ÷ before + −); no parentheses. Division by zero yields 0 for that step.
 */
export function evalFormula(f: any, metrics: Record<FormulaMetric, number>): number {
  const nf = normalizeFormula(f);
  const vals = nf.terms.map(t => (t.type === 'const' ? t.value : (metrics[t.metric] ?? 0)));

  // First pass: collapse × and ÷ into running operands.
  const v2: number[] = [vals[0]];
  const o2: FormulaOp[] = [];
  for (let i = 0; i < nf.ops.length; i++) {
    const op = nf.ops[i];
    const next = vals[i + 1];
    if (op === '*' || op === '/') {
      const prev = v2[v2.length - 1];
      v2[v2.length - 1] = op === '*' ? prev * next : (next === 0 ? 0 : prev / next);
    } else {
      o2.push(op);
      v2.push(next);
    }
  }

  // Second pass: + and −, left to right.
  let result = v2[0];
  for (let i = 0; i < o2.length; i++) {
    result = o2[i] === '+' ? result + v2[i + 1] : result - v2[i + 1];
  }
  const raw = Number.isFinite(result) ? result : 0;
  return applyRounding(raw, nf.rounding);
}
